import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  ToggleButtonGroup,
  ToggleButton,
  Chip
} from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';
import { TrendingUp, TrendingDown, TrendingFlat } from '@mui/icons-material';
import { brandColors } from '../../../../theme/theme';
import { formatPercentage, getVelocityIndicator } from '../../../../lib/utils/deepDiveUtils';

const AGE_COLORS = {
  'Under 35': brandColors.secondary,
  'Over 35': brandColors.primary
};

const AgeTrendChart = ({ trendData }) => {
  const [selectedMetric, setSelectedMetric] = useState('awareness');

  if (!trendData || Object.keys(trendData).length === 0) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="text.secondary">
          No age trend data available
        </Typography>
      </Paper>
    );
  }

  const metrics = [
    { key: 'awareness', label: 'Awareness' },
    { key: 'interest', label: 'Interest' },
    { key: 'theater', label: 'Theater Intent' },
    { key: 'payment', label: 'Willing to Pay' },
    { key: 'heat', label: 'Heat (7/7)' }
  ];

  // Prepare chart data
  const chartData = [];
  const maxWaves = Math.max(
    ...Object.values(trendData).map(trend => trend.data.length)
  );

  for (let i = 0; i < maxWaves; i++) {
    const dataPoint = { wave: i + 1 };
    
    Object.keys(AGE_COLORS).forEach(ageGroup => {
      if (trendData[ageGroup] && trendData[ageGroup].data[i]) {
        dataPoint[ageGroup] = trendData[ageGroup].data[i][selectedMetric];
      }
    });
    
    chartData.push(dataPoint);
  }

  // Get velocity indicators for current metric
  const velocityMetricKey = `${selectedMetric}Velocity`;
  const velocities = {};
  Object.keys(trendData).forEach(ageGroup => {
    if (trendData[ageGroup][velocityMetricKey]) {
      velocities[ageGroup] = trendData[ageGroup][velocityMetricKey];
    }
  });

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || payload.length === 0) return null;

    return (
      <Paper sx={{ p: 2, border: '1px solid #e0e0e0' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
          Round {label}
        </Typography>
        {payload.map((entry, index) => (
          <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                backgroundColor: entry.color
              }}
            />
            <Typography variant="body2">
              {entry.name}: <strong>{formatPercentage(entry.value)}</strong>
            </Typography>
          </Box>
        ))}
      </Paper>
    );
  };

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
        Age Group Trends Over Time
      </Typography>

      {/* Metric Selector */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
          Select Metric
        </Typography>
        <ToggleButtonGroup
          value={selectedMetric}
          exclusive
          onChange={(e, newMetric) => newMetric && setSelectedMetric(newMetric)}
          size="small"
        >
          {metrics.map(metric => (
            <ToggleButton key={metric.key} value={metric.key}>
              {metric.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      <Grid container spacing={3}>
        {/* Chart */}
        <Grid item xs={12} md={8}>
          <Box sx={{ height: 400 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis
                  dataKey="wave"
                  label={{ value: 'Survey Round', position: 'insideBottom', offset: -5 }}
                  tick={{ fontSize: 12 }}
                />
                <YAxis
                  domain={[0, 100]}
                  label={{ value: 'Percentage (%)', angle: -90, position: 'insideLeft' }}
                  tick={{ fontSize: 12 }}
                />
                <RechartsTooltip content={<CustomTooltip />} />
                <Legend />
                {Object.keys(AGE_COLORS).map(ageGroup => (
                  trendData[ageGroup] && (
                    <Line
                      key={ageGroup}
                      type="monotone"
                      dataKey={ageGroup}
                      stroke={AGE_COLORS[ageGroup]}
                      strokeWidth={3}
                      dot={{ r: 5 }}
                      activeDot={{ r: 7 }}
                      name={ageGroup}
                    />
                  )
                ))}
              </LineChart>
            </ResponsiveContainer>
          </Box>
        </Grid>

        {/* Velocity Cards */}
        <Grid item xs={12} md={4}>
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
            Trend Direction
          </Typography>
          <Grid container spacing={2}>
            {Object.keys(AGE_COLORS).map(ageGroup => {
              if (!trendData[ageGroup]) return null;

              const velocity = velocities[ageGroup];
              const velocityIndicator = getVelocityIndicator(velocity);
              const latestValue = trendData[ageGroup].data[trendData[ageGroup].data.length - 1];

              return (
                <Grid item xs={12} key={ageGroup}>
                  <Card
                    sx={{
                      border: `2px solid ${AGE_COLORS[ageGroup]}`,
                      backgroundColor: AGE_COLORS[ageGroup] + '10'
                    }}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            backgroundColor: AGE_COLORS[ageGroup]
                          }}
                        />
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          {ageGroup}
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        {velocity && velocity.direction === 'increasing' && (
                          <TrendingUp sx={{ color: '#4caf50', fontSize: 24 }} />
                        )}
                        {velocity && velocity.direction === 'decreasing' && (
                          <TrendingDown sx={{ color: '#f44336', fontSize: 24 }} />
                        )}
                        {(!velocity || velocity.direction === 'stable') && (
                          <TrendingFlat sx={{ color: '#757575', fontSize: 24 }} />
                        )}
                        <Typography variant="h5" sx={{ fontWeight: 700 }}>
                          {formatPercentage(latestValue ? latestValue[selectedMetric] : 0)}
                        </Typography>
                      </Box>

                      {velocity && (
                        <Box>
                          <Chip
                            label={velocityIndicator.text}
                            size="small"
                            sx={{
                              backgroundColor: velocityIndicator.color + '20',
                              color: velocityIndicator.color,
                              fontWeight: 600
                            }}
                          />
                          {velocity.latestChange !== 0 && (
                            <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                              {velocity.latestChange > 0 ? '+' : ''}{velocity.latestChange.toFixed(1)}pp
                              {' '}in latest round
                            </Typography>
                          )}
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Grid>
      </Grid>

      {/* Summary */}
      <Box sx={{ mt: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
        <Typography variant="caption" color="text.secondary">
          <strong>Age Group Trends:</strong> Comparing Under 35 vs Over 35 audiences across all survey rounds. 
          Track how different age demographics respond over time.
        </Typography>
      </Box>
    </Paper>
  );
};

export default AgeTrendChart;

